import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Activity,
  AlertTriangle,
  ArrowRight,
  BarChart3,
  CheckCircle2,
  Clock,
  Database,
  Eye,
  FileText,
  Fingerprint,
  GitBranch,
  LayoutDashboard,
  Loader2,
  LockKeyhole,
  RefreshCw,
  ShieldAlert,
  ShieldCheck,
  TrendingUp,
  Users,
} from 'lucide-react';
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';

import Loader from '../components/Loader';
import ErrorMessage from '../components/ErrorMessage';
import { authAPI, piiAPI } from '../services/api';

const ROUTES = {
  dashboard: '/dashboard',
  piiRecords: '/records',
  documents: '/records',
  platformUsers: '/principals',
  auditLogs: '/audit-logs',
  consents: '/records',
};

const CHART_COLORS = {
  orange: '#ea580c',
  red: '#dc2626',
  amber: '#d97706',
  emerald: '#059669',
  slate: '#64748b',
  blue: '#2563eb',
  purple: '#7c3aed',
};

const DONUT_COLORS = [
  '#64748b',
  '#ea580c',
  '#dc2626',
  '#d97706',
  '#059669',
  '#2563eb',
  '#7c3aed',
];

function cx(...classes) {
  return classes.filter(Boolean).join(' ');
}

function safeArray(value) {
  return Array.isArray(value) ? value : [];
}

function formatNumber(value) {
  const number = Number(value || 0);
  if (Number.isNaN(number)) return '0';
  return number.toLocaleString();
}

function formatPercent(value) {
  const number = Number(value || 0);
  if (Number.isNaN(number)) return '0%';
  return `${Math.max(0, Math.min(100, Math.round(number)))}%`;
}

function formatDate(value) {
  if (!value) return '—';

  try {
    return new Date(value).toLocaleString();
  } catch {
    return value;
  }
}

function getNestedValue(object, path, fallback = 0) {
  const value = path
    .split('.')
    .reduce((current, key) => (current ? current[key] : undefined), object);

  return value ?? fallback;
}

function normalizePermissions(profile, stats) {
  const user = profile || {};
  const actor = stats?.actor || {};

  const userType = user.user_type || actor.user_type || '';
  const role = String(user.role || actor.role || '').toLowerCase();

  const orgRole = user.org_admin_role || user.org_role || {};
  const rolePermissions = orgRole.permissions || user.permissions || [];
  const dashboardPermissions = stats?.permissions || {};

  const isAllAccess =
    user.is_superuser === true ||
    role === 'super_admin' ||
    role === 'superadmin' ||
    role === 'platform_admin' ||
    userType === 'platform_admin' ||
    userType === 'super_admin' ||
    userType === 'superadmin';

  const hasPermission = (permission) => {
    if (isAllAccess) return true;
    if (dashboardPermissions[permission] === true) return true;
    if (Array.isArray(rolePermissions) && rolePermissions.includes(permission)) return true;
    return false;
  };

  return {
    userType,
    role,
    isAllAccess,
    hasPermission,
  };
}

function buildVisibility(permissions) {
  return {
    platformUsers: permissions.hasPermission('view_principals'),
    pii:
      permissions.hasPermission('view_pii_masked') ||
      permissions.hasPermission('view_pii_metadata') ||
      permissions.hasPermission('view_pii_full'),
    documents:
      permissions.hasPermission('view_document_metadata') ||
      permissions.hasPermission('view_documents'),
    consent:
      permissions.hasPermission('view_consent_context') ||
      permissions.hasPermission('view_consent_snapshot') ||
      permissions.hasPermission('view_consents'),
    audit: permissions.hasPermission('search_audit_logs'),
    processing:
      permissions.hasPermission('view_processing_activities') ||
      permissions.hasPermission('view_ropa'),
  };
}

function getDashboardNumbers(stats) {
  return {
    piiTotal:
      getNestedValue(stats, 'cards.pii_records.value', null) ??
      stats?.pii_total ??
      0,

    docsTotal:
      getNestedValue(stats, 'cards.documents.value', null) ??
      stats?.docs_total ??
      0,

    platformUsers:
      getNestedValue(stats, 'cards.platform_users.value', null) ??
      stats?.platform_users_total ??
      0,

    submissionsToday:
      getNestedValue(stats, 'cards.submissions_today.value', null) ??
      stats?.submissions_today ??
      0,

    activeConsents:
      getNestedValue(stats, 'cards.active_consents.value', null) ??
      stats?.active_consents ??
      0,

    criticalRecords:
      stats?.attention?.critical_records ??
      stats?.critical_records ??
      0,

    missingConsent:
      stats?.attention?.records_missing_consent ??
      0,

    auditWarnings:
      stats?.attention?.audit_warning_events ??
      0,
  };
}

function buildSubmissionTrend(stats) {
  if (Array.isArray(stats?.submission_trend)) {
    return stats.submission_trend.map((item) => ({
      label: item.label || item.day || item.date,
      submissions: Number(item.submissions || item.count || 0),
      documents: Number(item.documents || 0),
    }));
  }

  return Object.entries(stats?.submission_data || {}).map(([day, count]) => ({
    label: day,
    submissions: Number(count || 0),
    documents: 0,
  }));
}

function buildDocumentTypeData(stats) {
  return safeArray(stats?.document_summary)
    .map((item) => ({
      name: item.document_type || item.name || 'Unknown',
      count: Number(item.count || 0),
    }))
    .filter((item) => item.count > 0);
}

function buildSensitivityData(stats) {
  return safeArray(stats?.sensitivity_summary)
    .map((item) => ({
      name: item.sensitivity || item.name || 'UNKNOWN',
      count: Number(item.count || 0),
    }))
    .filter((item) => item.count > 0);
}

function buildConsentStatusData(stats) {
  return safeArray(stats?.consent_status_summary)
    .map((item) => ({
      name: item.status || item.name || 'UNKNOWN',
      count: Number(item.count || 0),
    }))
    .filter((item) => item.count > 0);
}

function buildGovernanceHealth(stats, numbers) {
  const piiTotal = Number(numbers.piiTotal || 0);
  const docsTotal = Number(numbers.docsTotal || 0);
  const platformUsers = Number(numbers.platformUsers || 0);

  const consentCoverage =
    stats?.governance_health?.consent_coverage_pct ??
    (piiTotal > 0
      ? Math.max(0, Math.round(((piiTotal - numbers.missingConsent) / piiTotal) * 100))
      : 0);

  const documentEvidence =
    stats?.governance_health?.document_evidence_pct ??
    (piiTotal > 0
      ? Math.max(0, Math.min(100, Math.round((docsTotal / piiTotal) * 100)))
      : 0);

  const identityMapping =
    stats?.governance_health?.identity_mapping_pct ??
    (piiTotal > 0 && platformUsers > 0 ? 100 : 0);

  const fieldInventory =
    stats?.governance_health?.field_inventory_coverage_pct ??
    (piiTotal > 0 ? 100 : 0);

  return {
    consentCoverage,
    documentEvidence,
    identityMapping,
    fieldInventory,
    rows: [
      {
        label: 'Consent coverage',
        value: consentCoverage,
        description: 'PII records with linked consent context.',
      },
      {
        label: 'Document evidence',
        value: documentEvidence,
        description: 'Document evidence coverage across submitted records.',
      },
      {
        label: 'Identity mapping',
        value: identityMapping,
        description: 'Records linked to platform user profiles.',
      },
      {
        label: 'Field inventory',
        value: fieldInventory,
        description: 'Records with field-level metadata available.',
      },
    ],
  };
}

function WidgetCard({
  title,
  value,
  valueSuffix = '',
  subtitle,
  insight,
  icon: Icon,
  tone = 'slate',
  onClick,
  visible = true,
}) {
  if (!visible) return null;

  const toneClass =
    tone === 'red'
      ? 'bg-red-50 text-red-700 ring-red-100'
      : tone === 'emerald'
        ? 'bg-emerald-50 text-emerald-700 ring-emerald-100'
        : tone === 'amber'
          ? 'bg-amber-50 text-amber-700 ring-amber-100'
          : tone === 'blue'
            ? 'bg-blue-50 text-blue-700 ring-blue-100'
            : tone === 'orange'
              ? 'bg-orange-50 text-orange-700 ring-orange-100'
              : 'bg-slate-100 text-slate-700 ring-slate-200';

  const display =
    valueSuffix === '%'
      ? formatPercent(value)
      : `${formatNumber(value)}${valueSuffix}`;

  return (
    <button
      onClick={onClick}
      className="group w-full rounded-3xl border border-slate-200 bg-white p-5 text-left shadow-sm transition hover:-translate-y-0.5 hover:border-slate-300 hover:shadow-md"
    >
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <p className="text-sm font-semibold text-slate-500">{title}</p>

          <p className="mt-2 text-3xl font-black text-slate-950">
            {display}
          </p>

          {subtitle && (
            <p className="mt-1 text-xs font-medium text-slate-500">
              {subtitle}
            </p>
          )}
        </div>

        <div className={cx('rounded-2xl p-3 ring-1', toneClass)}>
          <Icon className="h-5 w-5" />
        </div>
      </div>

      {insight && (
        <div className="mt-4 rounded-2xl bg-slate-50 p-3">
          <p className="text-xs leading-5 text-slate-600">{insight}</p>
        </div>
      )}

      <div className="mt-4 flex items-center justify-between border-t border-slate-100 pt-3 text-xs font-bold text-slate-500">
        <span>Open workspace</span>
        <ArrowRight className="h-4 w-4 transition group-hover:translate-x-0.5 group-hover:text-orange-600" />
      </div>
    </button>
  );
}

function SectionHeader({ title, description, icon: Icon }) {
  return (
    <div className="mb-4 flex items-start justify-between gap-4">
      <div className="flex items-start gap-3">
        {Icon && (
          <div className="rounded-xl bg-slate-100 p-2 text-slate-700 ring-1 ring-slate-200">
            <Icon className="h-4 w-4" />
          </div>
        )}

        <div>
          <h2 className="font-black text-slate-950">{title}</h2>
          {description && (
            <p className="mt-1 text-sm text-slate-500">{description}</p>
          )}
        </div>
      </div>
    </div>
  );
}

function DashboardTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-3 shadow-lg">
      <p className="mb-2 text-xs font-bold uppercase tracking-wide text-slate-400">
        {label}
      </p>

      <div className="space-y-1">
        {payload.map((item) => (
          <div key={item.dataKey || item.name} className="flex items-center justify-between gap-6">
            <span className="text-xs text-slate-500">{item.name || item.dataKey}</span>
            <span className="text-sm font-bold text-slate-950">
              {formatNumber(item.value)}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function EmptyState({ message }) {
  return (
    <div className="flex h-[260px] items-center justify-center rounded-2xl border border-dashed border-slate-300 bg-slate-50">
      <p className="text-sm text-slate-500">{message}</p>
    </div>
  );
}

function ChartCard({ title, description, icon: Icon, children }) {
  return (
    <section className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
      <SectionHeader title={title} description={description} icon={Icon} />
      {children}
    </section>
  );
}

function SubmissionTrendChart({ data }) {
  if (!data.length) {
    return <EmptyState message="No submission trend data available yet." />;
  }

  return (
    <ResponsiveContainer width="100%" height={300}>
      <AreaChart data={data}>
        <defs>
          <linearGradient id="submissionGradientFinal" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor={CHART_COLORS.orange} stopOpacity={0.2} />
            <stop offset="95%" stopColor={CHART_COLORS.orange} stopOpacity={0.02} />
          </linearGradient>
        </defs>

        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
        <XAxis dataKey="label" stroke="#64748b" fontSize={12} />
        <YAxis stroke="#64748b" fontSize={12} allowDecimals={false} />
        <Tooltip content={<DashboardTooltip />} />

        <Area
          type="monotone"
          dataKey="submissions"
          name="Submissions"
          stroke={CHART_COLORS.orange}
          fill="url(#submissionGradientFinal)"
          strokeWidth={3}
        />

        <Area
          type="monotone"
          dataKey="documents"
          name="Documents"
          stroke={CHART_COLORS.slate}
          fill="transparent"
          strokeWidth={2}
        />
      </AreaChart>
    </ResponsiveContainer>
  );
}

function DonutChart({ data, nameKey = 'name', valueKey = 'count', emptyMessage }) {
  if (!data.length) {
    return <EmptyState message={emptyMessage} />;
  }

  return (
    <ResponsiveContainer width="100%" height={300}>
      <PieChart>
        <Pie
          data={data}
          dataKey={valueKey}
          nameKey={nameKey}
          cx="50%"
          cy="50%"
          innerRadius={64}
          outerRadius={98}
          paddingAngle={3}
        >
          {data.map((entry, index) => (
            <Cell key={entry[nameKey]} fill={DONUT_COLORS[index % DONUT_COLORS.length]} />
          ))}
        </Pie>

        <Tooltip content={<DashboardTooltip />} />
        <Legend />
      </PieChart>
    </ResponsiveContainer>
  );
}

function DocumentBarChart({ data }) {
  if (!data.length) {
    return <EmptyState message="No document type data available yet." />;
  }

  return (
    <ResponsiveContainer width="100%" height={300}>
      <BarChart data={data}>
        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
        <XAxis dataKey="name" stroke="#64748b" fontSize={12} />
        <YAxis stroke="#64748b" fontSize={12} allowDecimals={false} />
        <Tooltip content={<DashboardTooltip />} />

        <Bar
          dataKey="count"
          name="Documents"
          fill={CHART_COLORS.slate}
          radius={[10, 10, 0, 0]}
        />
      </BarChart>
    </ResponsiveContainer>
  );
}

function AttentionItem({
  title,
  message,
  value,
  icon: Icon,
  tone = 'orange',
  onClick,
  visible = true,
}) {
  if (!visible) return null;

  const toneClass =
    tone === 'red'
      ? 'border-red-200 bg-red-50 text-red-800'
      : tone === 'amber'
        ? 'border-amber-200 bg-amber-50 text-amber-800'
        : tone === 'emerald'
          ? 'border-emerald-200 bg-emerald-50 text-emerald-800'
          : 'border-orange-200 bg-orange-50 text-orange-800';

  return (
    <button
      onClick={onClick}
      className={cx(
        'group w-full rounded-2xl border p-4 text-left transition hover:-translate-y-0.5 hover:shadow-md',
        toneClass,
      )}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <Icon className="mt-0.5 h-5 w-5 shrink-0" />

          <div>
            <p className="text-sm font-black">{title}</p>
            <p className="mt-1 text-sm">{message}</p>

            <div className="mt-3 inline-flex items-center gap-1 text-xs font-black">
              Open related review
              <ArrowRight className="h-3.5 w-3.5 transition group-hover:translate-x-0.5" />
            </div>
          </div>
        </div>

        <span className="rounded-full bg-white/75 px-2.5 py-1 text-xs font-black">
          {formatNumber(value)}
        </span>
      </div>
    </button>
  );
}

function HealthRow({ label, value, description }) {
  const safeValue = Math.max(0, Math.min(100, Number(value || 0)));

  return (
    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
      <div className="mb-2 flex items-center justify-between gap-3">
        <div>
          <p className="text-sm font-bold text-slate-950">{label}</p>
          <p className="mt-1 text-xs text-slate-500">{description}</p>
        </div>

        <span className="rounded-full bg-white px-2.5 py-1 text-xs font-black text-slate-700 ring-1 ring-slate-200">
          {safeValue}%
        </span>
      </div>

      <div className="h-2 overflow-hidden rounded-full bg-white ring-1 ring-slate-200">
        <div
          className={cx(
            'h-full rounded-full',
            safeValue >= 90
              ? 'bg-emerald-500'
              : safeValue >= 60
                ? 'bg-orange-500'
                : 'bg-red-500',
          )}
          style={{ width: `${safeValue}%` }}
        />
      </div>
    </div>
  );
}

function RecentActivity({ items, visible }) {
  if (!visible) return null;

  return (
    <section className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
      <SectionHeader
        title="Recent activity"
        description="Latest operational activity visible to your role."
        icon={Activity}
      />

      <div className="space-y-3">
        {safeArray(items).length === 0 ? (
          <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-8 text-center">
            <Clock className="mx-auto h-8 w-8 text-slate-300" />
            <p className="mt-2 text-sm text-slate-500">No recent activity available.</p>
          </div>
        ) : (
          items.slice(0, 8).map((item, index) => (
            <div
              key={`${item.type || 'event'}-${item.id || index}`}
              className="rounded-2xl border border-slate-200 bg-slate-50 p-4"
            >
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="text-sm font-bold text-slate-950">
                    {item.title || item.type || 'Activity'}
                  </p>

                  <p className="mt-1 text-xs text-slate-500">
                    {item.description || item.subtitle || 'No additional details'}
                  </p>
                </div>

                <span className="shrink-0 rounded-full bg-white px-2.5 py-1 text-xs font-semibold text-slate-600 ring-1 ring-slate-200">
                  {formatDate(item.timestamp || item.created_at || item.event_time)}
                </span>
              </div>
            </div>
          ))
        )}
      </div>
    </section>
  );
}

function ReviewLane({ title, description, icon: Icon, route, onClick, visible = true }) {
  if (!visible) return null;

  return (
    <button
      onClick={() => onClick(route)}
      className="group rounded-2xl border border-slate-200 bg-white p-4 text-left shadow-sm transition hover:-translate-y-0.5 hover:border-slate-300 hover:shadow-md"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <div className="rounded-xl bg-slate-100 p-2 text-slate-700 ring-1 ring-slate-200">
            <Icon className="h-4 w-4" />
          </div>

          <div>
            <p className="text-sm font-black text-slate-950">{title}</p>
            <p className="mt-1 text-xs leading-5 text-slate-500">{description}</p>
          </div>
        </div>

        <ArrowRight className="h-4 w-4 text-slate-300 transition group-hover:translate-x-0.5 group-hover:text-orange-600" />
      </div>
    </button>
  );
}

export default function DashboardPage() {
  const navigate = useNavigate();

  const [stats, setStats] = useState(null);
  const [profile, setProfile] = useState(null);

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const [error, setError] = useState('');

  const loadDashboard = async () => {
    const isInitial = !stats;

    if (isInitial) setLoading(true);
    else setRefreshing(true);

    setError('');

    try {
      const [statsResult, profileResult] = await Promise.allSettled([
        piiAPI.getDashboardStats(),
        authAPI.getProfile(),
      ]);

      if (statsResult.status === 'fulfilled') {
        setStats(statsResult.value.data || {});
      } else {
        throw statsResult.reason;
      }

      if (profileResult.status === 'fulfilled') {
        setProfile(profileResult.value.data || null);
      }
    } catch (err) {
      setError(
        err.response?.data?.detail ||
          err.response?.data?.error ||
          err.message ||
          'Failed to load dashboard.',
      );
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    loadDashboard();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const permissions = useMemo(
    () => normalizePermissions(profile, stats || {}),
    [profile, stats],
  );

  const visibility = useMemo(
    () => buildVisibility(permissions),
    [permissions],
  );

  const numbers = useMemo(
    () => getDashboardNumbers(stats || {}),
    [stats],
  );

  const trendData = useMemo(
    () => buildSubmissionTrend(stats || {}),
    [stats],
  );

  const documentTypeData = useMemo(
    () => buildDocumentTypeData(stats || {}),
    [stats],
  );

  const sensitivityData = useMemo(
    () => buildSensitivityData(stats || {}),
    [stats],
  );

  const consentStatusData = useMemo(
    () => buildConsentStatusData(stats || {}),
    [stats],
  );

  const governanceHealth = useMemo(
    () => buildGovernanceHealth(stats || {}, numbers),
    [stats, numbers],
  );

  const reviewTotal =
    Number(numbers.criticalRecords || 0) +
    Number(numbers.missingConsent || 0) +
    Number(numbers.auditWarnings || 0);

  const organizationName =
    stats?.organization?.name ||
    profile?.organization_name ||
    profile?.organization?.name ||
    'Current organization';

  const userName =
    profile?.full_name ||
    profile?.name ||
    stats?.actor?.full_name ||
    profile?.email ||
    'Admin';

  const roleLabel =
    permissions.isAllAccess
      ? 'Full platform access'
      : profile?.org_admin_role?.admin_type
        ? `${profile.org_admin_role.admin_type.replaceAll('_', ' ')} admin`
        : profile?.user_type || 'Role-based access';

  const goTo = (route) => {
    navigate(route);
  };

  if (loading) {
    return <Loader text="Loading dashboard..." />;
  }

  if (error) {
    return (
      <div className="p-6">
        <ErrorMessage message={error} onRetry={loadDashboard} />
      </div>
    );
  }

  return (
    <div className="space-y-6 bg-slate-50/60">
      <section className="rounded-[2rem] border border-slate-200 bg-white p-6 shadow-sm">
        <div className="flex flex-col gap-6 xl:flex-row xl:items-center xl:justify-between">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full bg-slate-100 px-3 py-1 text-xs font-bold text-slate-700 ring-1 ring-slate-200">
              <LayoutDashboard className="h-3.5 w-3.5 text-orange-600" />
              Privacy operations dashboard
            </div>

            <h1 className="mt-4 text-3xl font-black tracking-tight text-slate-950">
              Welcome back, {userName}
            </h1>

            <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-500">
              A role-aware command center for platform users, PII records, documents,
              consent evidence, field-level governance, and audit review.
            </p>

            <div className="mt-5 flex flex-wrap gap-2">
              <span className="rounded-full bg-slate-900 px-3 py-1 text-xs font-bold text-white">
                {organizationName}
              </span>

              <span className="rounded-full bg-white px-3 py-1 text-xs font-bold text-slate-700 ring-1 ring-slate-200">
                {roleLabel}
              </span>

              {permissions.isAllAccess && (
                <span className="inline-flex items-center gap-1 rounded-full bg-red-50 px-3 py-1 text-xs font-bold text-red-700 ring-1 ring-red-100">
                  <LockKeyhole className="h-3 w-3" />
                  Elevated access
                </span>
              )}
            </div>
          </div>

          <div className="w-full rounded-[1.5rem] border border-slate-200 bg-slate-50 p-5 xl:max-w-md">
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-sm font-bold text-slate-950">Operational snapshot</p>
                <p className="mt-1 text-xs leading-5 text-slate-500">
                  {reviewTotal > 0
                    ? `${reviewTotal} visible item(s) need admin review.`
                    : 'No immediate review items visible to your role.'}
                </p>
              </div>

              <button
                onClick={loadDashboard}
                className="inline-flex items-center gap-2 rounded-xl bg-slate-900 px-4 py-2.5 text-sm font-bold text-white hover:bg-slate-800"
              >
                {refreshing ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <RefreshCw className="h-4 w-4" />
                )}
                Refresh
              </button>
            </div>

            <div className="mt-5 grid grid-cols-2 gap-3">
              <button
                onClick={() => goTo(ROUTES.piiRecords)}
                className="rounded-2xl bg-white p-4 text-left ring-1 ring-slate-200 transition hover:shadow-sm"
              >
                <p className="text-xs font-bold uppercase text-slate-500">Today</p>
                <p className="mt-1 text-2xl font-black text-slate-950">
                  {formatNumber(numbers.submissionsToday)}
                </p>
                <p className="mt-1 text-xs text-slate-500">new submissions</p>
              </button>

              <button
                onClick={() => {
                  if (numbers.auditWarnings > 0 && visibility.audit) goTo(ROUTES.auditLogs);
                  else goTo(ROUTES.piiRecords);
                }}
                className="rounded-2xl bg-white p-4 text-left ring-1 ring-slate-200 transition hover:shadow-sm"
              >
                <p className="text-xs font-bold uppercase text-slate-500">Review</p>
                <p className="mt-1 text-2xl font-black text-slate-950">
                  {formatNumber(reviewTotal)}
                </p>
                <p className="mt-1 text-xs text-slate-500">actionable items</p>
              </button>
            </div>
          </div>
        </div>
      </section>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
        <WidgetCard
          title="Platform Users"
          value={numbers.platformUsers}
          subtitle="Mapped user profiles"
          insight="Open consolidated user profiles with linked records, consent snapshots, and family links."
          icon={Users}
          visible={visibility.platformUsers}
          onClick={() => goTo(ROUTES.platformUsers)}
        />

        <WidgetCard
          title="PII Records"
          value={numbers.piiTotal}
          subtitle="Governed submitted records"
          insight="Review record metadata, identity context, field inventory, and secure PII access."
          icon={Database}
          tone="amber"
          visible={visibility.pii}
          onClick={() => goTo(ROUTES.piiRecords)}
        />

        <WidgetCard
          title="Consent Coverage"
          value={governanceHealth.consentCoverage}
          valueSuffix="%"
          subtitle="Records linked to consent"
          insight="Open records and verify consent snapshot, privacy notice title, and consent ID linkage."
          icon={CheckCircle2}
          tone="emerald"
          visible={visibility.consent}
          onClick={() => goTo(ROUTES.consents)}
        />

        <WidgetCard
          title="Secure Access Review"
          value={numbers.auditWarnings}
          subtitle="Warning / critical audit events"
          insight="Open audit evidence with actual fields accessed, access mode, purpose, reason, and justification."
          icon={ShieldAlert}
          tone="red"
          visible={visibility.audit}
          onClick={() => goTo(ROUTES.auditLogs)}
        />

        <WidgetCard
          title="Documents"
          value={numbers.docsTotal}
          subtitle="Attached document evidence"
          insight="Open records with document evidence and governed document viewer context."
          icon={FileText}
          visible={visibility.documents}
          onClick={() => goTo(ROUTES.documents)}
        />

        <WidgetCard
          title="Identity Mapping"
          value={governanceHealth.identityMapping}
          valueSuffix="%"
          subtitle="Records mapped to users"
          insight="Open platform users to review full name, email, employee ID, consent and family links."
          icon={GitBranch}
          tone="blue"
          visible={visibility.platformUsers}
          onClick={() => goTo(ROUTES.platformUsers)}
        />

        <WidgetCard
          title="Field Inventory"
          value={governanceHealth.fieldInventory}
          valueSuffix="%"
          subtitle="Field metadata coverage"
          insight="Open PII records and review field inventory, returned fields, masked fields and denied fields."
          icon={Fingerprint}
          tone="orange"
          visible={visibility.pii}
          onClick={() => goTo(ROUTES.piiRecords)}
        />

        <WidgetCard
          title="Submissions Today"
          value={numbers.submissionsToday}
          subtitle="New records today"
          insight="Open recently submitted PII bundles and verify consent/document linkage."
          icon={TrendingUp}
          tone="blue"
          visible={visibility.pii}
          onClick={() => goTo(ROUTES.piiRecords)}
        />
      </div>

      <section className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
        <SectionHeader
          title="Review lanes"
          description="Each lane opens the exact area where an admin can investigate or take action."
          icon={Eye}
        />

        <div className="grid grid-cols-1 gap-4 xl:grid-cols-4">
          <ReviewLane
            title="PII governance"
            description="Records, field inventory, sensitivity, and secure PII view."
            icon={Database}
            route={ROUTES.piiRecords}
            onClick={goTo}
            visible={visibility.pii}
          />

          <ReviewLane
            title="Platform users"
            description="Consolidated users, employee IDs, identities, family links."
            icon={Users}
            route={ROUTES.platformUsers}
            onClick={goTo}
            visible={visibility.platformUsers}
          />

          <ReviewLane
            title="Audit evidence"
            description="Sensitive access events, actual accessed fields, purpose and justification."
            icon={Activity}
            route={ROUTES.auditLogs}
            onClick={goTo}
            visible={visibility.audit}
          />

          <ReviewLane
            title="Document evidence"
            description="Document metadata, secure document viewing, and processing status."
            icon={FileText}
            route={ROUTES.documents}
            onClick={goTo}
            visible={visibility.documents}
          />
        </div>
      </section>

      <section className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
        <SectionHeader
          title="Needs attention"
          description="Click an item to go directly to the related review area."
          icon={AlertTriangle}
        />

        <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
          <AttentionItem
            title="Critical PII records"
            message="Records containing critical field classifications are present."
            value={numbers.criticalRecords}
            icon={ShieldAlert}
            tone="red"
            visible={visibility.pii && numbers.criticalRecords > 0}
            onClick={() => goTo(ROUTES.piiRecords)}
          />

          <AttentionItem
            title="Missing consent context"
            message="Some PII records are missing linked consent snapshot context."
            value={numbers.missingConsent}
            icon={AlertTriangle}
            tone="orange"
            visible={visibility.consent && numbers.missingConsent > 0}
            onClick={() => goTo(ROUTES.piiRecords)}
          />

          <AttentionItem
            title="Audit events needing review"
            message="Warning or critical audit events are available for review."
            value={numbers.auditWarnings}
            icon={Activity}
            tone="red"
            visible={visibility.audit && numbers.auditWarnings > 0}
            onClick={() => goTo(ROUTES.auditLogs)}
          />

          {reviewTotal === 0 && (
            <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4 text-emerald-800 xl:col-span-2">
              <div className="flex items-start gap-3">
                <ShieldCheck className="mt-0.5 h-5 w-5" />
                <div>
                  <p className="text-sm font-black">No immediate review items</p>
                  <p className="mt-1 text-sm">
                    Based on the information available to your role, there are no high-priority dashboard warnings right now.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-[0.8fr_1.2fr]">
        <section className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
          <SectionHeader
            title="Governance health"
            description="Coverage indicators across consent, document evidence, identity mapping, and field inventory."
            icon={ShieldCheck}
          />

          <div className="space-y-3">
            {governanceHealth.rows.map((item) => (
              <HealthRow
                key={item.label}
                label={item.label}
                value={item.value}
                description={item.description}
              />
            ))}
          </div>
        </section>

        <ChartCard
          title="Submission and document trend"
          description="PII submissions and document uploads over the recent period."
          icon={BarChart3}
        >
          <SubmissionTrendChart data={trendData} />
        </ChartCard>
      </div>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
        <ChartCard
          title="Document distribution"
          description="Breakdown of submitted document types."
          icon={FileText}
        >
          {visibility.documents ? (
            <DonutChart
              data={documentTypeData}
              emptyMessage="No document distribution data available."
            />
          ) : (
            <EmptyState message="You do not have permission to view document metadata." />
          )}
        </ChartCard>

        <ChartCard
          title="Field sensitivity"
          description="Sensitivity distribution from field inventory metadata."
          icon={ShieldAlert}
        >
          {visibility.pii ? (
            <DonutChart
              data={sensitivityData}
              emptyMessage="No field sensitivity data available."
            />
          ) : (
            <EmptyState message="You do not have permission to view field sensitivity metadata." />
          )}
        </ChartCard>

        <ChartCard
          title="Consent status"
          description="Consent snapshot distribution by current status."
          icon={CheckCircle2}
        >
          {visibility.consent ? (
            <DonutChart
              data={consentStatusData}
              emptyMessage="No consent status data available."
            />
          ) : (
            <EmptyState message="You do not have permission to view consent context." />
          )}
        </ChartCard>
      </div>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-[1fr_1fr]">
        <ChartCard
          title="Document uploads by type"
          description="Document activity grouped by type."
          icon={Database}
        >
          {visibility.documents ? (
            <DocumentBarChart data={documentTypeData} />
          ) : (
            <EmptyState message="You do not have permission to view document metadata." />
          )}
        </ChartCard>

        <RecentActivity
          visible={
            visibility.pii ||
            visibility.documents ||
            visibility.consent ||
            visibility.audit
          }
          items={stats?.recent_activity || []}
        />
      </div>
    </div>
  );
}